import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  processCollectionCover,
  processItemImage,
  processCustomFieldImage,
  processImage,
  generateImageFilename,
  IMAGE_SIZES,
  IMAGE_QUALITY,
  type ProcessedImage,
} from '@/utils/imageUtils';

const STORAGE_BUCKET = 'collection-images';

export type ImageType = 'collection-cover' | 'item' | 'custom-field' | 'feedback';

interface UploadResult {
  url: string;
  path: string;
}

interface ModerationResult {
  approved: boolean;
  reason?: string;
  category?: string;
  error?: string;
}

export interface ModerationError {
  type: 'moderation';
  reason: string;
  category?: string;
}

interface UseImageUploadReturn {
  uploading: boolean;
  error: string | null;
  moderationError: ModerationError | null;
  uploadImage: (file: File, type: ImageType, prefix?: string) => Promise<UploadResult | null>;
  uploadMultipleImages: (files: File[], type: ImageType, prefix?: string) => Promise<UploadResult[]>;
  deleteImage: (path: string) => Promise<boolean>;
  clearModerationError: () => void;
}

/**
 * Convert a blob to base64 data URL
 */
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Moderate an image using AI before upload
 */
const moderateImage = async (imageBlob: Blob): Promise<ModerationResult> => {
  try {
    const base64 = await blobToBase64(imageBlob);
    
    const { data, error } = await supabase.functions.invoke('moderate-image', {
      body: { imageBase64: base64 }
    });

    if (error) {
      console.error('Moderation function error:', error);
      // Fail-open: if moderation fails, allow upload
      return { approved: true };
    }

    return data as ModerationResult;
  } catch (err) {
    console.error('Moderation error:', err);
    // Fail-open: if moderation fails, allow upload
    return { approved: true };
  }
};

export function useImageUpload(): UseImageUploadReturn {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [moderationError, setModerationError] = useState<ModerationError | null>(null);

  const clearModerationError = useCallback(() => {
    setModerationError(null);
  }, []);

  /**
   * Process an image file based on its intended use
   */
  const processImageByType = async (file: File, type: ImageType): Promise<ProcessedImage> => {
    switch (type) {
      case 'collection-cover':
        return processCollectionCover(file);
      case 'item':
        return processItemImage(file);
      case 'custom-field':
        return processCustomFieldImage(file);
      case 'feedback':
        return processImage(file, IMAGE_SIZES.feedback, IMAGE_QUALITY.standard);
      default:
        return processItemImage(file);
    }
  };

  /**
   * Upload a single image to storage
   */
  const uploadImage = useCallback(async (
    file: File,
    type: ImageType,
    prefix: string = type
  ): Promise<UploadResult | null> => {
    if (!user) {
      setError('You must be logged in to upload images');
      return null;
    }

    setUploading(true);
    setError(null);
    setModerationError(null);

    try {
      // Process the image (resize and compress)
      const processed = await processImageByType(file, type);
      
      // Moderate the image before upload
      const moderation = await moderateImage(processed.blob);
      
      if (!moderation.approved) {
        setModerationError({
          type: 'moderation',
          reason: moderation.reason || 'This image was flagged as inappropriate',
          category: moderation.category,
        });
        return null;
      }
      
      // Generate unique filename
      const filePath = generateImageFilename(user.id, prefix);
      
      // Upload to storage
      const { data, error: uploadError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(filePath, processed.blob, {
          contentType: 'image/jpeg',
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from(STORAGE_BUCKET)
        .getPublicUrl(data.path);

      return {
        url: publicUrl,
        path: data.path,
      };
    } catch (err) {
      console.error('Image upload error:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload image');
      return null;
    } finally {
      setUploading(false);
    }
  }, [user]);

  /**
   * Upload multiple images to storage
   */
  const uploadMultipleImages = useCallback(async (
    files: File[],
    type: ImageType,
    prefix: string = type
  ): Promise<UploadResult[]> => {
    if (!user) {
      setError('You must be logged in to upload images');
      return [];
    }

    setUploading(true);
    setError(null);

    const results: UploadResult[] = [];

    try {
      for (const file of files) {
        const result = await uploadImage(file, type, prefix);
        if (result) {
          results.push(result);
        }
      }
      return results;
    } catch (err) {
      console.error('Multiple image upload error:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload images');
      return results;
    } finally {
      setUploading(false);
    }
  }, [user, uploadImage]);

  /**
   * Delete an image from storage
   */
  const deleteImage = useCallback(async (path: string): Promise<boolean> => {
    try {
      const { error: deleteError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .remove([path]);

      if (deleteError) {
        throw deleteError;
      }

      return true;
    } catch (err) {
      console.error('Image delete error:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete image');
      return false;
    }
  }, []);

  return {
    uploading,
    error,
    moderationError,
    uploadImage,
    uploadMultipleImages,
    deleteImage,
    clearModerationError,
  };
}
